import React, { useLayoutEffect, useRef, useState } from "react"
import { fitLabels } from "../utils/fitLabels.utils"

const GAP_PX = 8

// Una fila di pastiglie che accorcia i nomi quando serve a non andare a
// capo. Ogni pastiglia si disegna due volte, di nascosto, intera e
// accorciata, per sapere quanto è larga davvero — padding, font e tondini
// compresi — e da lì si decide quali mostrare accorciate. Si rifà quando la
// scheda cambia larghezza e quando arrivano i font.
//
// `items` sono `{ key, full, short }`; `renderPill(item, label)` disegna la
// pastiglia con il nome che le si passa.
function FittedPills({ items, renderPill }) {
	const containerRef = useRef(null)
	const measureRefs = useRef({})
	const [rowWidth, setRowWidth] = useState(0)
	const [shortened, setShortened] = useState({})

	// la larghezza della riga, e le sue variazioni
	useLayoutEffect(() => {
		const container = containerRef.current
		if (!container) return
		setRowWidth(container.clientWidth)

		if (typeof ResizeObserver === "undefined") return
		const observer = new ResizeObserver(() => setRowWidth(container.clientWidth))
		observer.observe(container)
		return () => observer.disconnect()
	}, [])

	// i font arrivano dopo: fino ad allora le misure sono di quello di ripiego
	const [fontsReady, setFontsReady] = useState(false)
	useLayoutEffect(() => {
		let isCurrent = true
		document.fonts?.ready?.then(() => isCurrent && setFontsReady(true))
		return () => {
			isCurrent = false
		}
	}, [])

	const itemsKey = items.map(item => `${item.key}:${item.full}`).join("|")

	useLayoutEffect(() => {
		if (!rowWidth) return
		// per eccesso: mezzo pixel arrotondato in giù basta a mandare a capo
		const widthOf = key =>
			Math.ceil(measureRefs.current[key]?.getBoundingClientRect().width ?? 0)
		const widths = items.map(item => ({
			full: widthOf(`${item.key}:full`),
			short: widthOf(`${item.key}:short`),
		}))
		const result = fitLabels(widths, rowWidth, GAP_PX)
		setShortened(Object.fromEntries(items.map((item, i) => [item.key, result[i]])))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowWidth, itemsKey, fontsReady])

	return (
		<div ref={containerRef} style={{ position: "relative" }}>
			<div style={{ display: "flex", flexWrap: "wrap", gap: `${GAP_PX}px` }}>
				{items.map(item => (
					<React.Fragment key={item.key}>
						{renderPill(item, shortened[item.key] ? item.short : item.full)}
					</React.Fragment>
				))}
			</div>

			{/* le misure: invisibili, fuori dal flusso e dai tocchi */}
			<div
				aria-hidden="true"
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					// larga quanto serve: una pastiglia misurata non deve andare
					// a capo dentro di sé
					width: "max-content",
					visibility: "hidden",
					pointerEvents: "none",
					height: 0,
					overflow: "hidden",
				}}>
				{items.map(item =>
					["full", "short"].map(variant => (
						<div
							key={`${item.key}:${variant}`}
							ref={node => {
								measureRefs.current[`${item.key}:${variant}`] = node
							}}
							style={{ display: "inline-flex", whiteSpace: "nowrap" }}>
							{renderPill(item, item[variant])}
						</div>
					)),
				)}
			</div>
		</div>
	)
}

export default FittedPills
